import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function SettingsUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "user" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setForm({ email: "", password: "", name: "", role: "user" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div data-testid="settings-users">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-heading font-bold text-xl text-espresso-900 tracking-tight">Team members</h2>
          <p className="text-taupe-600 text-sm mt-1">{users.length} users</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold h-11"
              data-testid="add-user-button"
            >
              <Plus className="w-4 h-4 mr-2" /> Add user
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Add user</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="add-user-form">
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                  data-testid="user-name-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Email</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                  data-testid="user-email-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Password</Label>
                <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                  data-testid="user-password-input" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-espresso-800 mb-2 block">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="rounded-xl h-11 bg-taupe-100 border-taupe-200" data-testid="user-role-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user" data-testid="user-role-option-user">User</SelectItem>
                    <SelectItem value="admin" data-testid="user-role-option-admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={submitting}
                  className="rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold"
                  data-testid="submit-user-button">
                  {submitting ? "Creating…" : "Create user"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-taupe-100 text-taupe-600 text-xs font-semibold uppercase tracking-wider">
                <th className="text-left py-4 px-6">Name</th>
                <th className="text-left py-4 px-6">Email</th>
                <th className="text-left py-4 px-6">Role</th>
                <th className="text-right py-4 px-6" />
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={4} className="py-6 text-center text-taupe-500 text-sm">Loading…</td></tr>}
              {users.map((u) => (
                <tr key={u.id} className="border-t border-taupe-200 hover:bg-taupe-100/40 transition-colors" data-testid="user-row">
                  <td className="py-4 px-6 text-sm text-espresso-900 font-medium">{u.name || <span className="text-taupe-500">—</span>}</td>
                  <td className="py-4 px-6 text-sm text-taupe-700">{u.email}</td>
                  <td className="py-4 px-6 text-sm">
                    <span className={`inline-flex px-2.5 py-1 rounded-md text-xs font-semibold capitalize ${
                      u.role === "admin" ? "bg-espresso-100 text-espresso-800" : "bg-taupe-100 text-taupe-700"
                    }`}>{u.role}</span>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <button
                      onClick={() => remove(u.id)}
                      disabled={u.id === currentUser?.id}
                      className="p-2 rounded-lg text-terracotta-500 hover:bg-terracotta-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`delete-user-${u.id}`}
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
